import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { notifyError } from "../lib/ui";
import {
  createSubscription,
  deleteSubscription,
  listSubscriptions,
} from "../api/endpoints";
import type { Category, Subscription } from "../types";
import { CATEGORY_LABELS } from "../types";
import { HandBlot } from "../components/Hand";

// Размеры, доступные для фильтра подписки.
const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "One size"];

export default function SubscriptionsPage() {
  // Из каталога сюда приходят с уже заполненными условиями: человек искал
  // категорию и размер, получил пустую выдачу — заставлять его набирать то же
  // самое ещё раз значит потерять его на форме.
  const [searchParams] = useSearchParams();
  const prefilled = !!(searchParams.get("category") || searchParams.get("size") || searchParams.get("price_max"));

  const [subs, setSubs] = useState<Subscription[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Форма новой подписки.
  const [category, setCategory] = useState<Category | "">(
    () => (searchParams.get("category") ?? "") as Category | "");
  const [size, setSize] = useState(() => searchParams.get("size") ?? "");
  const [priceMax, setPriceMax] = useState(() => searchParams.get("price_max") ?? "");
  const [formOpen, setFormOpen] = useState(prefilled);

  const refresh = () =>
    listSubscriptions().then(setSubs).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    refresh();
  }, []);

  const add = async () => {
    setBusy(true);
    try {
      await createSubscription({
        category: category || undefined,
        size: size || undefined,
        price_max: priceMax ? Number(priceMax) : undefined,
      });
      // Сбрасываем форму и перезагружаем список.
      setCategory("");
      setSize("");
      setPriceMax("");
      setFormOpen(false);
      await refresh();
    } catch (e) {
      notifyError(e);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    setBusy(true);
    try {
      await deleteSubscription(id);
      setSubs((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      notifyError(e);
    } finally {
      setBusy(false);
    }
  };

  // Формирует читаемое описание подписки для карточки.
  const renderLabel = (s: Subscription) => {
    const parts: string[] = [];
    if (s.category) parts.push(CATEGORY_LABELS[s.category]);
    if (s.size) parts.push(`размер ${s.size}`);
    if (s.price_max) parts.push(`до ${Math.round(s.price_max)} ₽`);
    return parts.length ? parts.join(" · ") : "Любые новинки";
  };

  return (
    <div className="container">
      <h1>Подписки</h1>
      <p style={{ color: "var(--hint)", marginTop: 0, fontSize: 14 }}>
        Бот уведомит вас когда появится подходящий товар.
      </p>

      {/* Список активных подписок. */}
      {loading && <p style={{ color: "var(--hint)" }}>Загрузка…</p>}
      {!loading && subs.length === 0 && !formOpen && (
        <div className="empty">
          <HandBlot variant={3} className="empty__blot" />
          <p style={{ color: "var(--hint)", margin: 0 }}>Подписок пока нет.</p>
        </div>
      )}
      {subs.map((s) => (
        <div
          key={s.id}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 12px",
            background: "var(--secondary-bg)",
            borderRadius: 12,
            marginBottom: 8,
          }}
        >
          {/* Условия подписки — перечень фильтров, а не фраза: микро-слой. */}
          <span className="micro">🔔 {renderLabel(s)}</span>
          <button
            onClick={() => remove(s.id)}
            disabled={busy}
            style={{
              background: "none",
              border: "none",
              color: "var(--hint)",
              fontSize: 18,
              cursor: "pointer",
              padding: "0 4px",
              lineHeight: 1,
            }}
            title="Удалить подписку"
          >
            ✕
          </button>
        </div>
      ))}

      {/* Форма добавления подписки. */}
      {formOpen ? (
        <div
          style={{
            padding: 16,
            background: "var(--secondary-bg)",
            borderRadius: 16,
            marginTop: 12,
          }}
        >
          <h3 className="section-label">Новая подписка</h3>
          <p style={{ fontSize: 13, color: "var(--hint)", marginTop: 0 }}>
            {prefilled
              ? "Условия перенесли из каталога — проверьте и подпишитесь."
              : "Оставьте поле пустым — фильтр по нему работать не будет."}
          </p>

          <div className="field">
            <label>Категория</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as Category | "")}
            >
              <option value="">Любая</option>
              {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Размер</label>
            <select value={size} onChange={(e) => setSize(e.target.value)}>
              <option value="">Любой</option>
              {SIZES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Максимальная цена ₽</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="Без ограничения"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
            />
          </div>

          <button className="btn" onClick={add} disabled={busy}>
            Подписаться
          </button>
          <div style={{ height: 8 }} />
          <button
            className="btn btn--secondary"
            onClick={() => setFormOpen(false)}
            disabled={busy}
          >
            Отмена
          </button>
        </div>
      ) : (
        <button
          className="btn"
          style={{ marginTop: 12 }}
          onClick={() => setFormOpen(true)}
        >
          + Добавить подписку
        </button>
      )}
    </div>
  );
}
